import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface WasteTonnageChartProps {
  location: string;
  embedOnly?: boolean;
  onWasteChangeCalculated?: (changePercent: number | null) => void;
}

// Double y-axis time series: tons_waste (left), waste_per_service_population (right)
const WasteTonnageChart: React.FC<WasteTonnageChartProps> = ({ location, embedOnly = false, onWasteChangeCalculated }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      console.warn('WasteTonnageChart: no geofips for', location);
      creatingRef.current = false;
      return;
    }

    const { data } = await supabaseClient
      .from('total_waste')
      .select('year, tons_waste, waste_per_service_population')
      .eq('geofips', geofips)
      .order('year', { ascending: true });

    // Fetch California average waste_per_service_population (geofips = 6)
    const { data: californiaData, error: californiaError } = await supabaseClient
      .from('total_waste')
      .select('year, waste_per_service_population')
      .eq('geofips', 6)
      .order('year', { ascending: true });
    
    if (californiaError) {
      console.warn('WasteTonnageChart: Error fetching California data:', californiaError);
    } else {
      console.log('WasteTonnageChart: California data fetched:', californiaData?.length, 'rows');
    }

    // Fetch population data for backcasting
    const { data: populationData } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('geofips', geofips)
      .order('year', { ascending: true });

    // Create year range 2005-2023
    const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
    
    // Build data by year
    const wasteDataByYear: Record<number, { tons: number; perService: number }> = {};
    (data || []).forEach((r: any) => {
      wasteDataByYear[r.year] = {
        tons: Number(r.tons_waste || 0),
        perService: Number(r.waste_per_service_population || 0)
      };
    });

    // Build population by year
    const populationByYear: Record<number, number> = {};
    (populationData || []).forEach((r: any) => {
      populationByYear[r.year] = Number(r.population || 0);
    });

    // Find earliest year with waste data
    const wasteYears = Object.keys(wasteDataByYear).map(y => parseInt(y)).sort((a, b) => a - b);
    const earliestWasteYear = wasteYears.length > 0 ? Math.min(...wasteYears) : null;

    // Backcast waste data to 2005 using population ratios
    if (earliestWasteYear && earliestWasteYear > 2005 && populationByYear[earliestWasteYear] && populationByYear[earliestWasteYear] > 0) {
      const baseTons = wasteDataByYear[earliestWasteYear].tons;
      const basePerService = wasteDataByYear[earliestWasteYear].perService;
      const basePopulation = populationByYear[earliestWasteYear];

      // Backcast years before earliest waste data year
      for (let year = 2005; year < earliestWasteYear; year++) {
        const yearPopulation = populationByYear[year] || basePopulation;
        const populationRatio = yearPopulation / basePopulation;
        
        // Backcast tons using population ratio
        wasteDataByYear[year] = {
          tons: baseTons * populationRatio,
          perService: basePerService // Per service population can remain constant or use population ratio if we have service population data
        };
      }
    }

    const years = allYears;
    const tons = years.map(year => wasteDataByYear[year]?.tons || 0);
    const perService = years.map(year => wasteDataByYear[year]?.perService || 0);
    
    // Calculate change since 2005 for smart title
    if (onWasteChangeCalculated && tons.length > 0) {
      const waste2005 = tons[0] || 0;
      const waste2023 = tons[tons.length - 1] || 0;
      if (waste2005 > 0) {
        const changePercent = ((waste2023 - waste2005) / waste2005) * 100;
        onWasteChangeCalculated(changePercent);
      } else {
        onWasteChangeCalculated(null);
      }
    }
    
    // Determine which years are backcasted (extrapolated)
    const originalDataYears = (data || []).map((r: any) => r.year);
    const extrapolatedStartYear = earliestWasteYear && earliestWasteYear > 2005 ? 2005 : null;
    const extrapolatedEndYear = earliestWasteYear && earliestWasteYear > 2005 ? earliestWasteYear - 1 : null;
    
    // Split data into actual and extrapolated portions
    const createDataset = (label: string, dataValues: (number | null)[], color: string, yAxisID: string, isPerService: boolean = false) => {
      const datasets = [];
      
      if (extrapolatedStartYear !== null && extrapolatedEndYear !== null) {
        // Extrapolated portion (dotted) - include transition year to connect seamlessly
        const extrapolatedData = years.map((year, idx) => {
          if (year >= extrapolatedStartYear && year <= extrapolatedEndYear + 1) {
            return dataValues[idx];
          }
          return null;
        });
        datasets.push({
          label: `${label} (Extrapolated)`,
          data: extrapolatedData,
          borderColor: color,
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          tension: 0.2,
          yAxisID: yAxisID,
          pointRadius: 0,
          borderWidth: 2,
        });
      }
      
      // Actual data portion (solid) - include transition year to connect seamlessly
      const actualData = years.map((year, idx) => {
        if (extrapolatedStartYear !== null && extrapolatedEndYear !== null && year <= extrapolatedEndYear) {
          return null;
        }
        return dataValues[idx];
      });
      
      // Add transition point to actual data for seamless connection
      if (extrapolatedEndYear !== null) {
        const transitionYearIdx = years.indexOf(extrapolatedEndYear + 1);
        if (transitionYearIdx >= 0 && transitionYearIdx < actualData.length) {
          actualData[transitionYearIdx] = dataValues[transitionYearIdx];
        }
      }
      datasets.push({
        label: label,
        data: actualData,
        borderColor: color,
        backgroundColor: isPerService ? 'transparent' : (color === '#2563eb' ? 'rgba(37,99,235,0.25)' : color === '#10b981' ? 'rgba(16,185,129,0.25)' : 'rgba(0,0,0,0.1)'),
        borderDash: isPerService ? [5, 5] : [],
        tension: 0.2,
        yAxisID: yAxisID,
        pointRadius: 0,
        borderWidth: 2,
      });
      
      return datasets;
    };
    
    // Map California data to match years
    const californiaPerService = years.map(year => {
      const calYearData = (californiaData || []).find((r: any) => r.year === year);
      return calYearData ? Number(calYearData.waste_per_service_population || 0) : null;
    });

    const datasets = [
      ...createDataset('Tons of Waste', tons, '#2563eb', 'y', false),
      ...createDataset(`${location} (per service pop)`, perService, '#10b981', 'y1', true),
    ];
    
    // Add California average (always dashed, not extrapolated)
    datasets.push({
      label: 'California Average (per service pop)',
      data: californiaPerService,
      borderColor: '#f59e0b',
      backgroundColor: 'transparent',
      borderDash: [5, 5],
      tension: 0.2,
      yAxisID: 'y1',
      pointRadius: 0,
      borderWidth: 2,
    });

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: 10,
            top: 5,
            left: 5,
            right: 5
          }
        },
        plugins: {
          legend: { 
            display: true,
            position: 'bottom', 
            labels: { boxWidth: 12, padding: 8, font: { size: 10 } } 
          },
          title: {
            display: !embedOnly,
            text: `Waste Tonnage and Per-Service Population (${location})`,
            font: { size: 16, weight: 'bold' },
          },
        },
        scales: {
          y: {
            type: 'linear',
            position: 'left',
            title: { display: true, text: 'Tons of Waste' },
            beginAtZero: true,
          },
          y1: {
            type: 'linear',
            position: 'right',
            grid: { drawOnChartArea: false },
            title: { display: true, text: 'Waste per Service Population' },
            beginAtZero: true,
          },
          x: { 
            title: { display: true, text: 'Year' },
            ticks: { font: { size: 10 } }
          },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });
    creatingRef.current = false;
  }, [location, embedOnly, onWasteChangeCalculated]);

  useEffect(() => {
    buildChart();
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasRef.current) {
          const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  return (
    <div className="chart-container" style={{ height: embedOnly ? '100%' : '400px', width: '100%' }}>
      <canvas ref={canvasRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default WasteTonnageChart;


